import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, Users, Briefcase, ChevronDown, Star, Check } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { motion } from '../../lib/motion';
import { Category, ProviderProfile } from '../../types';
import { categoryService } from '../../services/category.service';
import { locationService } from '../../services/location.service';
import { providerService } from '../../services/provider.service';
import { LocationInput } from '../ui/LocationInput';
import { getCategoryIcon } from '../../utils/categoryIcons';

interface HomeSearchProps {
    className?: string;
}

const POPULAR_CATEGORY_SLUGS = ['plomberie', 'electricite', 'platrerie-staff', 'peinture'];

const FALLBACK_CATEGORY_LABELS: Record<string, string> = {
    plomberie: 'Plomberie',
    electricite: 'Electricite',
    'platrerie-staff': 'Platrerie & Staff',
    peinture: 'Peinture',
};

export const HomeSearch: React.FC<HomeSearchProps> = ({ className }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState<'service' | 'name'>('service');

    // Form state
    const [categoryId, setCategoryId] = useState('');
    const [showCategoryMenu, setShowCategoryMenu] = useState(false);
    const [cityId, setCityId] = useState('');
    const [cityText, setCityText] = useState('');
    const [query, setQuery] = useState('');
    // First render uses the prerendered catalogue/cities when there are some; both are refreshed on mount.
    const [categories, setCategories] = useState<Category[]>(() => categoryService.peekAllCategories() ?? []);
    const [cities, setCities] = useState<any[]>(() => locationService.peekAllCities() ?? []); // Use flexible type or City if imported

    // Suggestions state
    const [suggestions, setSuggestions] = useState<ProviderProfile[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // Load initial data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [cats, cityData] = await Promise.all([
                categoryService.getAllCategories(),
                locationService.getAllCities()
            ]);
            setCategories(cats ?? []);
            setCities(cityData ?? []);
        } catch (error) {
            console.error('Failed to load search data', error);
        }
    };

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (categoryId) params.append('categoryId', categoryId);
        if (cityId) params.append('cityId', cityId);
        // Pass cityText as cityName if available (for "Tout le Maroc" or custom text)
        if (cityText) params.append('cityName', cityText);
        if (query) params.append('q', query);

        navigate(`/search?${params.toString()}`);
    };

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setQuery(val);

        if (activeTab === 'name' && val.length > 2) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(async () => {
                try {
                    const results = await providerService.suggestProviders({ q: val, cityId: cityId || undefined });
                    setSuggestions(results);
                    setShowSuggestions(true);
                } catch (err) {
                    console.error("Suggest error", err);
                }
            }, 300);
        } else {
            setShowSuggestions(false);
        }
    };

    // Helper for city name
    const getCityName = (city: any) => {
        return (['ar', 'darija_ar'].includes(i18n.language) ? city.nameAr : city.name) || city.name;
    };

    const getCategoryName = (cat: Category) => cat.names?.[i18n.language] || cat.names?.['fr'] || cat.slug;
    const selectedCategory = categories.find((cat) => cat.id === categoryId);

    const popularCategories = POPULAR_CATEGORY_SLUGS
        .map((slug) => categories.find((category) => category.slug === slug))
        .filter((category): category is Category => Boolean(category));

    return (
        <div className={`w-full max-w-4xl mx-auto ${className} relative z-40`}>
            {/* Tabs */}
            <div className="flex justify-center mb-6">
                <div className="bg-white/20 backdrop-blur-md p-1 rounded-full inline-flex">
                    <button
                        onClick={() => setActiveTab('service')}
                        className={`px-6 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'service'
                            ? 'bg-white text-primary shadow-lg'
                            : 'text-white hover:bg-white/10'
                            }`}
                    >
                        <Briefcase className="w-4 h-4" />
                        {t('search.byService')}
                    </button>
                    <button
                        onClick={() => setActiveTab('name')}
                        className={`px-6 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'name'
                            ? 'bg-white text-primary shadow-lg'
                            : 'text-white hover:bg-white/10'
                            }`}
                    >
                        <Users className="w-4 h-4" />
                        {t('search.byName')}
                    </button>
                </div>
            </div>

            {/* Search Box Container */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-6 relative">
                <div className="flex flex-col md:flex-row gap-4 items-end">

                    {activeTab === 'service' ? (
                        <>
                            {/* Block A: Category */}
                            <div className="flex-1 w-full relative z-[60]">
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 ml-1">
                                    {t('search.categoryLabel')}
                                </label>
                                {/* Custom menu — the native <select> list looked out of place */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowCategoryMenu(!showCategoryMenu)}
                                        aria-haspopup="listbox"
                                        aria-expanded={showCategoryMenu}
                                        className="group relative w-full h-14 ps-12 pe-10 bg-gray-50 hover:bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-bold text-gray-900 text-start truncate"
                                    >
                                        {selectedCategory ? getCategoryName(selectedCategory) : t('categories.all')}
                                        <Briefcase className="absolute start-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 group-hover:text-primary transition-colors" />
                                        <ChevronDown className={`absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 transition-transform ${showCategoryMenu ? 'rotate-180' : ''}`} />
                                    </button>

                                    {showCategoryMenu && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setShowCategoryMenu(false)} />
                                            <ul role="listbox" className="absolute z-50 mt-2 w-full min-w-[16rem] max-h-80 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 py-1 text-start">
                                                {[null, ...categories].map((cat) => {
                                                    const value = cat?.id ?? '';
                                                    const selected = value === categoryId;
                                                    return (
                                                        <li key={value || 'all'} role="option" aria-selected={selected}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setCategoryId(value);
                                                                    setShowCategoryMenu(false);
                                                                }}
                                                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-start ${selected ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}
                                                            >
                                                                <span className="w-5 h-5 shrink-0 flex items-center justify-center text-gray-500">
                                                                    {cat ? getCategoryIcon(cat.slug, cat.iconUrl, 'w-5 h-5') : <Briefcase className="w-5 h-5" />}
                                                                </span>
                                                                <span className="flex-1 truncate">{cat ? getCategoryName(cat) : t('categories.all')}</span>
                                                                {selected && <Check className="w-4 h-4 shrink-0" />}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Block B: Location */}
                            <div className="flex-1 w-full relative z-50">
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 ml-1">
                                    {t('search.locationLabel')}
                                </label>
                                <LocationInput
                                    value={{
                                        city: cityId ? (cities.find(c => c.id === cityId)?.name || '') : '',
                                        cityId: cityId,
                                        address: '', // Not needed for simple search
                                    }}
                                    onChange={(loc) => {
                                        if (loc.cityId) {
                                            setCityId(loc.cityId);
                                            setCityText(loc.city);
                                        } else if (loc.latitude && loc.longitude) {
                                            // Handle GPS/Precise location
                                            navigate(`/search?q=${encodeURIComponent(query)}&categoryId=${categoryId}&lat=${loc.latitude}&lon=${loc.longitude}&radius=5`);
                                        } else {
                                            setCityId('');
                                            setCityText(loc.city || '');
                                        }
                                    }}
                                    hideLabels
                                    hideAddress
                                    hideRegion
                                    placeholder={t('locationInput.selectCity')}
                                    icon={<MapPin className="w-5 h-5" />}
                                    className="w-full"
                                />
                            </div>
                        </>
                    ) : (
                        /* Block: Name Search */
                        <div className="flex-[2] w-full relative">
                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 ml-1">
                                {t('search.nameLabel')}
                            </label>
                            <div className="relative group">
                                <input
                                    type="text"
                                    value={query}
                                    onChange={handleQueryChange}
                                    onFocus={() => query.length > 2 && setShowSuggestions(true)}
                                    // Delay hiding to allow click
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    placeholder={t('search.namePlaceholder')}
                                    className="w-full h-14 pl-12 pr-4 bg-gray-100 border-none hover:bg-gray-200 focus:bg-white rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium text-gray-900 placeholder:text-gray-400"
                                />
                                <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 group-hover:text-primary transition-colors" />
                            </div>

                            {/* Suggestions Dropdown */}
                            <AnimatePresence>
                                {showSuggestions && suggestions.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 py-2"
                                    >
                                        {suggestions.map((provider) => (
                                            <div
                                                key={provider.id}
                                                onClick={() => navigate(`/providers/${provider.id}`)}
                                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 transition-colors"
                                            >
                                                <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden shrink-0">
                                                    {provider.avatarUrl ? (
                                                        <img src={provider.avatarUrl} alt={provider.displayName} className="w-full h-full object-cover"  loading="lazy"/>
                                                    ) : (
                                                        <Users className="w-5 h-5 m-2.5 text-gray-500" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-gray-900 truncate">{provider.businessName || provider.displayName}</div>
                                                    <div className="text-sm text-gray-500 flex items-center gap-2">
                                                        {provider.categories?.[0]?.names?.[i18n.language] || provider.categories?.[0]?.names?.['fr'] || 'Prestataire'}
                                                        {provider.averageRating && Number(provider.averageRating) > 0 && (
                                                            <span className="flex items-center text-yellow-500 text-xs">
                                                                <Star className="w-3 h-3 fill-current ml-1" /> {Number(provider.averageRating).toFixed(1)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {provider.distanceKm !== null && provider.distanceKm !== undefined && (
                                                    <div className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full whitespace-nowrap">
                                                        {provider.distanceKm < 1 ? '< 1 km' : `${Math.round(provider.distanceKm)} km`}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}

                    {/* Search Button */}
                    <button
                        onClick={handleSearch}
                        aria-label={t('common.search')}
                        className="w-full md:w-auto px-8 h-14 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/30 flex items-center justify-center gap-2 shrink-0"
                    >
                        <Search className="w-5 h-5" />
                        <span className="hidden md:inline">{t('common.search')}</span>
                    </button>
                </div>

                {/* Quick Filters / Chips (Optional) */}
                <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-500">
                    <span className="mr-2 pt-1">{t('search.popular')}</span>
                    {POPULAR_CATEGORY_SLUGS.map((slug) => {
                        const category = popularCategories.find((entry) => entry.slug === slug);
                        const label = category?.names?.[i18n.language] || category?.names?.['fr'] || FALLBACK_CATEGORY_LABELS[slug] || slug;

                        return (
                            <button
                                key={slug}
                                onClick={() => {
                                    setActiveTab('service');
                                    if (category) {
                                        setCategoryId(category.id);
                                        navigate(`/search?categoryId=${category.id}`);
                                    } else {
                                        setQuery(label);
                                        navigate(`/search?q=${encodeURIComponent(label)}`);
                                    }
                                }}
                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors text-xs font-medium"
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
